import * as React from 'react'
import {Lang} from 'yougile-lang'
import {ObjUtil, Util} from 'yougile-util'
import {
  DataObjectId,
  PermissionCustomRole,
  PermissionRoleKey
} from 'yougile-model'
import {cn} from '@containers/html-utils'
import {UserAccess} from '@extensions/user-access'
import {Backend} from '../../../common/backend'
import {EventBus} from '../../client/event-bus'
import {openProjectParticipants} from '../../client/events'
import {Sync} from '../../client/sync'
import {useBoolean, useGlobalKeyDown} from '../../containers/hooks'
import {DownArrow} from '../common/controls/down-arrow'
import {IconOkay} from '../common/controls/icon-okay'
import {Menu} from '../menu'

interface Props {
  value: PermissionRoleKey
  onChange(newValue: PermissionRoleKey): void
  addNewRole?: () => void
  className?: string
  children?: React.ReactNode
  customRoles?: PermissionCustomRole[]
  noAddingNewRole?: boolean
}

export const ProjectRole = (props: Props) => {
  const {
    value,
    className,
    onChange,
    addNewRole,
    children,
    customRoles = [],
    noAddingNewRole = !Backend.features.customRoles
  } = props
  const bem = Util.bemFrom('project-role', [className])
  const menuOpen = useBoolean(false)
  const click = (val: PermissionRoleKey) => () => {
    menuOpen.turnOff()
    onChange(val)
  }
  useGlobalKeyDown({
    escape: () => {
      onChange(value)
      return true
    }
  })

  const title = UserAccess.getRoleTitle(value, customRoles)
  return (
    <div className={bem()}>
      <div className={bem('title')} onClick={menuOpen.toggle}>
        <div className={bem('title-text')}>{children || title}</div>
        <DownArrow />
      </div>
      {menuOpen.value && (
        <Menu zIndex={1000} onEscape={menuOpen.turnOff}>
          <div className={bem('overlay')} onClick={menuOpen.turnOff} />
          <div className={bem('menu')}>
            {customRoles.map(r => {
              return (
                <div
                  key={r.key}
                  className={bem('option', {current: value === r.key})}
                  onClick={click(r.key)}
                >
                  <IconOkay />
                  {r.name}
                  <div className={bem('descr')}>
                    {r.description || Lang.msg.proj.roleNoDescr}
                  </div>
                </div>
              )
            })}
            <div
              className={bem('option', {current: value === 'admin'})}
              onClick={click('admin')}
            >
              <IconOkay />
              {Lang.msg.permission.adminRole}
              <div className={bem('descr')}>
                {Lang.msg.permission.adminRoleDescription}
              </div>
            </div>

            <div
              className={bem('option', {current: value === 'worker'})}
              onClick={click('worker')}
            >
              <IconOkay />
              {Lang.msg.permission.workerRole}
              <div className={bem('descr')}>
                {Lang.msg.permission.workerRoleDescription}
              </div>
            </div>

            <div
              className={bem('option', {current: value === 'observer'})}
              onClick={click('observer')}
            >
              <IconOkay />
              {Lang.msg.permission.observerRole}
              <div className={bem('descr')}>
                {Lang.msg.permission.observerRoleDescription}
              </div>
            </div>
            {!noAddingNewRole && (
              <div
                className={cn(bem('add'), 'select-none')}
                onClick={addNewRole}
              >
                {Lang.msg.proj.createNewRole}
              </div>
            )}
          </div>
        </Menu>
      )}
    </div>
  )
}

interface DirectiveProps {
  projectId: DataObjectId
  role: PermissionRoleKey
  setRole: (projectId: DataObjectId, newRole: PermissionRoleKey) => void
  close: () => void
}

export const ProjectRoleDirective = (props: DirectiveProps) => {
  const {projectId, role, setRole, close} = props
  const box = Sync.getCurrentSandbox()
  const project = box.getHub(projectId)
  const isAdded = useBoolean(true)
  const addRole = () => {
    isAdded.turnOff()
    close()
    EventBus.dispatch(openProjectParticipants(project, true))
  }
  const customRoles = ObjUtil.list(project.data.roles, (_, r) => r)
  if (!isAdded.value) {
    return null
  }
  const rolesSupported = Backend.features.customRoles
  const onChange = (r: PermissionRoleKey) => setRole(projectId, r)
  return (
    <ProjectRole
      addNewRole={addRole}
      onChange={onChange}
      customRoles={customRoles}
      value={role}
      noAddingNewRole={!rolesSupported}
    />
  )
}
